// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { EndpointDeviceWithAgentInfo } from '@api/types';
import { toIsoDateString } from '@utils/utils';

/**
 * Presentation-ready view model for one row of the endpoints table. Every field
 * is a display string so columns render without branching on `agentStatus`.
 * Group-synced endpoints (typically `agentStatus` UNKNOWN/UNSPECIFIED) omit
 * hostname/version/createdAt; those resolve to the caller-supplied fallback
 * label rather than blank, "0", or an epoch date (REQ-011).
 */
export type EndpointRow = {
  rowKey: string;
  hostname: string;
  version: string;
  installedDate: string;
};

/**
 * Maps a device to an `EndpointRow`. Kept pure: both the i18n fallback label
 * and the installed-date formatter are passed in from the callsite so no
 * translation dependency leaks into the mapping. `installedDate` reads
 * `createdAt` (the install timestamp) as `YYYY-MM-DD` and hands it to
 * `formatInstalledDate`, which applies the localized "Installed {{date}}"
 * template; absent dates fall back to `fallbackLabel` (REQ-011).
 *
 * The BFF contract carries no unique device id, so `rowKey` is a composite of
 * the identifying fields; it is stable for a given device but not guaranteed
 * globally unique across distinct group-synced endpoints that share all fields.
 */
export function toEndpointRow(
  device: EndpointDeviceWithAgentInfo,
  fallbackLabel: string,
  formatInstalledDate: (isoDate: string) => string,
): EndpointRow {
  const installedIso = device.createdAt ? toIsoDateString(new Date(device.createdAt)) : undefined;

  return {
    rowKey: [device.hostname ?? '', device.version ?? '', device.createdAt ?? '', ...(device.groupIds ?? [])].join('|'),
    hostname: device.hostname ?? fallbackLabel,
    version: device.version ?? fallbackLabel,
    installedDate: installedIso ? formatInstalledDate(installedIso) : fallbackLabel,
  };
}
